import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Valid section IDs for the PasskeyHelp page
const VALID_SECTION_IDS = [
  'what-is-passkey',
  'set-up',
  'login-without-passkey',
  'device-requirements',
  'contact-info',
  'verify-email-phone',
  'help-contacts'
] as const;

/**
 * Simplified hash expander hook that manages both state and URL synchronization
 */
export const useHashExpander = () => {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Get current hash from URL
  const currentHash = location.hash.substring(1);

  // Handle hash expansion and scrolling
  const handleHashExpansion = useCallback((hash: string) => {
    // Only expand valid section IDs
    if (hash && VALID_SECTION_IDS.includes(hash as any)) {
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.add(hash);
        return newSet;
      });

      // Wait for DOM to update after expansion, then scroll and focus
      requestAnimationFrame(() => {
        const element = document.getElementById(hash);
        if (element) {
          if (!element.hasAttribute('tabindex')) {
            element.setAttribute('tabindex', '-1');
          }

          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start'
          });

          // Use another frame for focus to ensure scroll has started
          requestAnimationFrame(() => element.focus());
        }
      });
    }
  }, []);

  // Handle React Router hash changes
  useEffect(() => {
    handleHashExpansion(currentHash);
  }, [currentHash, handleHashExpansion]);

  // Handle direct hash changes (pasting URLs in existing tabs)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      handleHashExpansion(hash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [handleHashExpansion]);

  // Check if a section is expanded
  const isExpanded = useCallback((sectionId: string) => {
    return expandedSections.has(sectionId);
  }, [expandedSections]);

  // Toggle section and update URL
  const toggleSection = useCallback((sectionId: string) => {
    const wasExpanded = expandedSections.has(sectionId);

    if (wasExpanded) {
      // Collapsing - always remove hash from URL when any section is closed
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });

      // Clear hash from URL whenever any expander is closed
      const newUrl = window.location.pathname;
      window.history.pushState(null, '', newUrl);
    } else {
      // Expanding
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.add(sectionId);
        return newSet;
      });

      // Update URL hash
      const newUrl = `${window.location.pathname}#${sectionId}`;
      window.history.pushState(null, '', newUrl);
    }
  }, [expandedSections]);

  return {
    toggleSection,
    isExpanded
  };
};

export default useHashExpander;
